import { useState } from 'react';
import { NO_AUTOFILL } from './FilterPopover';
import type { MultiSelectOption } from './MultiSelectFilterPopover';

interface Props {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Shown as "<heading> (n)"; "n selected" when omitted. */
  heading?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  autoFocusSearch?: boolean;
  /** Render labels in monospace (codes such as action names). */
  mono?: boolean;
}

/** Searchable checkbox list with a "select all visible" toggle. */
export function OptionChecklist({
  options,
  selected,
  onChange,
  heading,
  searchable = false,
  searchPlaceholder = 'Search…',
  autoFocusSearch = false,
  mono = false,
}: Props) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const visible = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const allVisibleSelected = visible.length > 0 && visible.every((o) => selected.includes(o.value));
  const toggleVisible = () => {
    if (allVisibleSelected) {
      onChange(selected.filter((v) => !visible.some((o) => o.value === v)));
    } else {
      onChange([...new Set([...selected, ...visible.map((o) => o.value)])]);
    }
  };

  return (
    <div className="column-filter-checklist">
      <div className="column-filter-checklist-header">
        <span>{heading ? `${heading} (${selected.length})` : `${selected.length} selected`}</span>
        <button type="button" className="column-filter-link" onClick={toggleVisible} disabled={visible.length === 0}>
          {allVisibleSelected ? 'Unselect visible' : 'Select all visible'}
        </button>
      </div>
      {searchable && (
        <input
          type="text"
          className="column-filter-input"
          {...NO_AUTOFILL}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          autoFocus={autoFocusSearch}
        />
      )}
      <div className="column-filter-options">
        {visible.length === 0 && <div className="column-filter-empty">No options</div>}
        {visible.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <label key={o.value} className={`column-filter-option ${checked ? 'checked' : ''} ${mono ? 'mono' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} />
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
